import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import { PageHeader } from '@/components/ui/PageHeader'
import { Bold, Italic, Underline, List, ListOrdered, Link, Save, FileText } from 'lucide-react'

interface EditNoteProps {
    noteId: string
    noteData: any
    onBack: () => void
    onSave: () => void
    onNavigateHome: () => void
    showConfirm: boolean
    onConfirmNavigateHome: () => void
    onCancelConfirm: () => void
}

export const EditNote = ({
    noteId,
    noteData,
    onBack,
    onSave,
    onNavigateHome,
    showConfirm,
    onConfirmNavigateHome,
    onCancelConfirm
}: EditNoteProps) => {
    const { user } = useAuth()
    const NOTE_TYPES = ['entretien', "réunion d’équipe", 'anamnèse', 'contacts réseau']
    const capitalizeFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

    const [note, setNote] = useState(noteData.note || '')
    const [noteType, setNoteType] = useState<string>(noteData.type || '')
    const [comment, setComment] = useState(noteData.comment || '')
    const [errors, setErrors] = useState<string[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const editorRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (editorRef.current && noteData.note) {
            editorRef.current.innerHTML = noteData.note
        }
    }, [noteData.note])

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!user?.id) {
            setErrors(['Utilisateur non authentifié'])
            return
        }

        const noteContent = editorRef.current?.innerHTML || ''
        const newErrors = []

        if (!noteContent.trim()) {
            newErrors.push('Le contenu de la note est requis')
        }
        if (!noteType) {
            newErrors.push('Le type de note est requis')
        }

        setErrors(newErrors)

        if (newErrors.length === 0) {
            setIsSubmitting(true)

            try {
                const { error } = await supabase
                    .from('notes')
                    .update({
                        note: noteContent,
                        type: noteType,
                        comment: comment || null,
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', noteId)

                if (error) {
                    throw error
                }

                // Call the onSave callback to refresh the notes list
                onSave()
            } catch (error) {
                console.error('Error updating note:', error)
                setErrors(['Erreur lors de la sauvegarde'])
            } finally {
                setIsSubmitting(false)
            }
        }
    }

    const executeCommand = (command: string, value?: string) => {
        document.execCommand(command, false, value)
        editorRef.current?.focus()
    }

    const insertBulletList = () => {
        executeCommand('insertUnorderedList')
    }

    const insertNumberedList = () => {
        executeCommand('insertOrderedList')
    }

    const insertLink = () => {
        const url = prompt('Entrez l\'URL du lien:')
        if (url) {
            executeCommand('createLink', url)
        }
    }

    const formatBold = () => executeCommand('bold')
    const formatItalic = () => executeCommand('italic')
    const formatUnderline = () => executeCommand('underline')

    return (
        <div className="min-h-screen bg-gray-50">
            <PageHeader title={`Modifier la note - ${noteData.locataire}`} icon={<FileText size={20} />} onBack={onBack} />
            <div className="max-w-4xl mx-auto py-8 px-4">

                <form onSubmit={handleSave} className="bg-white rounded-lg shadow p-8">
                    {errors.length > 0 && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                            <h3 className="text-red-800 font-medium">Erreurs :</h3>
                            <ul className="list-disc list-inside text-red-700">
                                {errors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Nom du.de la locataire
                            </label>
                            <input
                                type="text"
                                value={noteData.locataire}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Travailleur.euse
                            </label>
                            <input
                                type="text"
                                value={noteData.worker_name}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Collègue.s HF présent.e.s
                            </label>
                            <input
                                type="text"
                                value={Array.isArray(noteData.colleague_names) ? noteData.colleague_names.join(', ') : noteData.colleague_names || ''}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Date de la note
                            </label>
                            <input
                                type="date"
                                value={noteData.date}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Type de note <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={noteType}
                                onChange={e => setNoteType(e.target.value)}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${!noteType ? 'border-red-300' : 'border-gray-300'}`}
                            >
                                <option value="">— Sélectionner un type —</option>
                                {NOTE_TYPES.map(t => (
                                    <option key={t} value={t}>{capitalizeFirst(t)}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Note
                        </label>

                        {/* Formatting Toolbar */}
                        <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-t-md bg-gray-50">
                            <button
                                type="button"
                                onClick={formatBold}
                                className="p-2 hover:bg-gray-200 rounded"
                                title="Gras"
                            >
                                <Bold size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={formatItalic}
                                className="p-2 hover:bg-gray-200 rounded"
                                title="Italique"
                            >
                                <Italic size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={formatUnderline}
                                className="p-2 hover:bg-gray-200 rounded"
                                title="Souligné"
                            >
                                <Underline size={16} />
                            </button>
                            <div className="w-px bg-gray-300 mx-1"></div>
                            <button
                                type="button"
                                onClick={insertBulletList}
                                className="p-2 hover:bg-gray-200 rounded"
                                title="Liste à puces"
                            >
                                <List size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={insertNumberedList}
                                className="p-2 hover:bg-gray-200 rounded"
                                title="Liste numérotée"
                            >
                                <ListOrdered size={16} />
                            </button>
                            <div className="w-px bg-gray-300 mx-1"></div>
                            <button
                                type="button"
                                onClick={insertLink}
                                className="p-2 hover:bg-gray-200 rounded"
                                title="Insérer un lien"
                            >
                                <Link size={16} />
                            </button>
                        </div>

                        {/* Rich Text Editor */}
                        <div
                            ref={editorRef}
                            contentEditable
                            className="w-full min-h-[300px] px-3 py-2 border-l border-r border-b border-gray-300 rounded-b-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                            style={{
                                lineHeight: '1.5',
                                fontSize: '14px'
                            }}
                            onBlur={(e) => {
                                setNote(e.target.innerHTML)
                            }}
                        />

                        <div className="text-right text-sm text-gray-500 mt-1">
                            Utilisez la barre d'outils pour formater votre texte
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Commentaire additionnel
                        </label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            maxLength={1000}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Commentaire additionnel (optionnel, max 1000 caractères)"
                        />
                        <div className="text-right text-sm text-gray-500 mt-1">
                            {comment.length}/1000 caractères
                        </div>
                    </div>

                    <div className="flex justify-between">
                        <Button type="button" variant="outline" onClick={onBack}>
                            Annuler
                        </Button>
                        <div className="space-x-4">
                            <Button type="button" variant="outline" onClick={onNavigateHome}>
                                Accueil
                            </Button>
                            <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <>
                                        <Save className="mr-2 h-4 w-4 animate-spin" />
                                        Sauvegarde...
                                    </>
                                ) : (
                                    <>
                                        <Save className="mr-2 h-4 w-4" />
                                        Sauvegarder
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>

            {showConfirm && (
                <ConfirmDialog
                    onConfirm={onConfirmNavigateHome}
                    onCancel={onCancelConfirm}
                />
            )}
        </div>
    )
} 